import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Plus, Trash2, Sparkles, Loader2, CheckCircle2, ListChecks, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, SectionCard } from "@/components/Primitives";
import ComplianceShield from "@/components/ComplianceShield";
import AiAnswerCard from "@/components/AiAnswerCard";
import EHRCopyButton from "@/components/EHRCopyButton";

export default function DocumentationPro() {
  const navigate = useNavigate();
  const [phrases, setPhrases] = useState([]);
  const [newPhrase, setNewPhrase] = useState("");
  const [pending, setPending] = useState([]);
  const [sentence, setSentence] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [expanding, setExpanding] = useState(false);
  const [expandError, setExpandError] = useState(null);
  const [batchNoteId, setBatchNoteId] = useState(null);
  const [batchSentence, setBatchSentence] = useState("");
  const [batchExpanded, setBatchExpanded] = useState(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchError, setBatchError] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    const [p, n] = await Promise.all([api.get("/doc/phrase-library"), api.get("/doc/pending")]);
    setPhrases(p.data); setPending(n.data);
  };

  const addPhrase = async (e) => {
    e.preventDefault();
    if (!newPhrase.trim()) return;
    await api.post("/doc/phrase-library", { phrase: newPhrase });
    setNewPhrase("");
    loadAll();
    toast.success("Phrase added");
  };

  const deletePhrase = async (id) => {
    await api.delete(`/doc/phrase-library/${id}`);
    loadAll();
  };

  const expand = async (sentenceText, isBatch = false) => {
    if (isBatch) { setBatchLoading(true); setBatchError(null); } else { setExpanding(true); setExpandError(null); }
    try {
      const r = await api.post("/doc/expand", {
        sentence: sentenceText,
        note_type: "soap",
        phrase_library: phrases.map(p => p.phrase),
      });
      if (isBatch) setBatchExpanded(r.data); else setExpanded(r.data);
    } catch (err) {
      const msg = err.code === "ECONNABORTED"
        ? "ARIA took longer than 2 minutes to respond. Please try again — the model is usually faster after the first call."
        : err.response?.data?.detail || "AI expansion failed. Tap retry to try again.";
      if (isBatch) setBatchError(msg); else setExpandError(msg);
      toast.error(msg);
    } finally {
      if (isBatch) setBatchLoading(false); else setExpanding(false);
    }
  };

  const completeBatch = async () => {
    await api.post(`/doc/pending/${batchNoteId}/complete`);
    toast.success("Note completed — 9 min logged");
    setBatchNoteId(null); setBatchSentence(""); setBatchExpanded(null);
    loadAll();
  };

  const formatNote = (n) => {
    if (!n) return "";
    return `S: ${n.subjective}\n\nO: ${n.objective}\n\nA: ${n.assessment}\n\nP: ${n.plan}`;
  };

  return (
    <>
      <PageHeader
        eyebrow="Zero Documentation Burden"
        title="ARIA Documentation Pro"
        subtitle="One sentence in. Complete SOAP note out. Save phrases ARIA learns. Bulk-clear pending notes."
        testid="docpro-header"
      />

      {/* One-sentence to full SOAP */}
      <SectionCard title="One sentence → complete SOAP note" subtitle="Dictate or type one sentence; ARIA expands it using your saved phrases.">
        <textarea
          rows={2}
          value={sentence}
          onChange={(e) => setSentence(e.target.value)}
          placeholder="e.g. 'Follow-up DM2, A1c improved to 6.8 on metformin, continuing same plan.'"
          className="w-full px-3 py-2 border border-brand rounded-md text-sm"
          data-testid="docpro-sentence-input"
        />
        <button
          onClick={() => expand(sentence)}
          disabled={expanding || !sentence.trim()}
          className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:bg-[#35096B] disabled:opacity-50"
          data-testid="docpro-expand-btn"
        >
          {expanding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {expanding ? "ARIA expanding…" : "Expand to full SOAP note"}
        </button>
        {expandError && (
          <div className="mt-3 border border-red-200 bg-red-50 rounded-md p-3 flex items-start gap-2" data-testid="docpro-expand-error">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-xs text-red-900">
              <div className="font-semibold mb-1">ARIA expansion failed</div>
              <div>{expandError}</div>
            </div>
            <button onClick={() => expand(sentence)} className="px-2 py-1 rounded bg-red-700 text-white text-[11px] font-medium" data-testid="docpro-expand-retry">Retry</button>
          </div>
        )}

        {expanded && (
          <div className="mt-4 space-y-3" data-testid="docpro-expanded">
            <div className="border border-brand rounded-md p-4 bg-brand-bg/40 text-sm whitespace-pre-wrap font-mono leading-relaxed">{formatNote(expanded.note)}</div>
            {expanded.quality_flags?.length > 0 && (
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3" data-testid="docpro-quality-flags">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900 mb-1">
                  <AlertCircle className="w-3.5 h-3.5" /> Quality check — flagged items:
                </div>
                <ul className="text-xs text-amber-900 list-disc pl-5">
                  {expanded.quality_flags.map((f, i) => <li key={i}>{f}</li>)}
                </ul>
              </div>
            )}
            <ComplianceShield
              outputId={`docpro-${Date.now()}`}
              module="documentation"
              confidence={expanded.confidence}
              summary={`One-sentence SOAP expansion (${expanded.confidence} confidence)`}
            />
            <EHRCopyButton text={formatNote(expanded.note)} module="docpro-soap" label="Copy SOAP for EHR" />
            <AiAnswerCard
              platformModule="documentation-pro"
              originalQuery={sentence}
              originalAiResponse={formatNote(expanded.note)}
              retrievedEntryIds={expanded.retrieved_entry_ids || []}
              suggestedDomain="documentation"
              testid="docpro-answer-card"
            />
          </div>
        )}
      </SectionCard>

      <div className="h-6" />

      {/* Phrase library */}
      <SectionCard title={`Custom phrase library (${phrases.length})`} subtitle="Phrases ARIA will incorporate into future notes automatically.">
        <form onSubmit={addPhrase} className="flex gap-2 mb-3">
          <input
            value={newPhrase}
            onChange={(e) => setNewPhrase(e.target.value)}
            placeholder="e.g. 'Patient counseled on lifestyle modification.'"
            className="flex-1 px-3 py-2 border border-brand rounded-md text-sm"
            data-testid="phrase-input"
          />
          <button type="submit" className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-brand-primary text-white text-sm font-medium hover:bg-[#35096B]" data-testid="phrase-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        </form>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {phrases.length === 0 && <div className="text-sm text-brand-muted col-span-2">No saved phrases yet. Add the phrases you say in every note.</div>}
          {phrases.map(p => (
            <div key={p.id} className="flex items-center justify-between gap-2 p-2 border border-brand rounded-md bg-white" data-testid={`phrase-${p.id}`}>
              <div className="text-xs text-brand-text truncate">{p.phrase}</div>
              <button onClick={() => deletePhrase(p.id)} className="p-1 text-brand-muted hover:text-red-600" data-testid={`phrase-del-${p.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="h-6" />

      {/* Batch completion mode */}
      <SectionCard
        title={`Bulk completion mode (${pending.length} pending)`}
        subtitle="Clear 10 notes in 15 minutes. Tap a note, dictate one sentence, ARIA generates the rest."
      >
        {pending.length === 0 ? (
          <div className="text-sm text-emerald-700 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" />All pending notes complete. Excellent work.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {pending.map(n => (
              <button
                key={n.id}
                onClick={() => { setBatchNoteId(n.id); setBatchSentence(""); setBatchExpanded(null); }}
                className={`text-left p-3 border rounded-md transition-colors ${batchNoteId === n.id ? "border-brand-primary bg-brand-bg" : "border-brand bg-white hover:bg-brand-bg/40"}`}
                data-testid={`batch-note-${n.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <FileText className="w-3.5 h-3.5 text-brand-primary" />
                  <span className="text-xs font-medium text-brand-text">{n.patient}</span>
                  <span className="text-[10px] text-brand-muted ml-auto">{n.encounter_type}</span>
                </div>
                <div className="text-xs text-brand-muted line-clamp-2">{n.snippet}</div>
              </button>
            ))}
          </div>
        )}

        {batchNoteId && (
          <div className="mt-5 border border-brand-primary rounded-lg p-4 bg-brand-bg/30" data-testid="batch-active-note">
            <div className="text-xs eyebrow mb-2">Active note</div>
            <textarea
              rows={2}
              value={batchSentence}
              onChange={(e) => setBatchSentence(e.target.value)}
              placeholder="Dictate or type your one-sentence summary…"
              className="w-full px-3 py-2 border border-brand rounded-md text-sm"
              data-testid="batch-sentence-input"
            />
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => expand(batchSentence, true)}
                disabled={batchLoading || !batchSentence.trim()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-brand-primary text-white text-xs font-medium hover:bg-[#35096B] disabled:opacity-50"
                data-testid="batch-expand-btn"
              >
                {batchLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {batchLoading ? "ARIA expanding…" : "Expand with ARIA"}
              </button>
              {batchExpanded && (
                <>
                  <EHRCopyButton text={formatNote(batchExpanded.note)} module="batch" label="Copy" />
                  <button
                    onClick={completeBatch}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700"
                    data-testid="batch-complete-btn"
                  >
                    <ListChecks className="w-3.5 h-3.5" /> Mark complete (+9 min)
                  </button>
                </>
              )}
            </div>
            {batchError && (
              <div className="mt-3 border border-red-200 bg-red-50 rounded-md p-3 flex items-start gap-2" data-testid="batch-expand-error">
                <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
                <div className="flex-1 text-xs text-red-900">{batchError}</div>
                <button onClick={() => expand(batchSentence, true)} className="px-2 py-1 rounded bg-red-700 text-white text-[11px] font-medium" data-testid="batch-expand-retry">Retry</button>
              </div>
            )}
            {batchExpanded && (
              <div className="mt-3 border border-brand rounded-md p-3 bg-white text-xs whitespace-pre-wrap font-mono leading-relaxed" data-testid="batch-expanded">
                {formatNote(batchExpanded.note)}
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </>
  );
}
